// #region IMPORTS
import type { Bone, EulerOrder, Skeleton } from 'three';
import { Euler, Quaternion, Vector3 } from 'three';

// #endregion

// #region CONSTANTS
export const INC_POS_LIST = ['pelvis'];
const NL = '\n';
const TO_DEG = 180 / Math.PI;

// Use Z-X-Y rotation order for BVH
const ORDER: EulerOrder = 'ZXY';

// Common axes
const AX_X = new Vector3(1, 0, 0);
const AX_Y = new Vector3(0, 1, 0);
const AX_Z = new Vector3(0, 0, 1);

// Build an offset quaternion in the same order the BVH file encodes rotations.
function buildOffsetQuat(
  offs: [number?, number?, number?] | undefined,
  order: EulerOrder = ORDER,
): Quaternion {
  const ox = ((offs?.[0] || 0) * Math.PI) / 180;
  const oy = ((offs?.[1] || 0) * Math.PI) / 180;
  const oz = ((offs?.[2] || 0) * Math.PI) / 180;

  const qx = new Quaternion().setFromAxisAngle(AX_X, ox);
  const qy = new Quaternion().setFromAxisAngle(AX_Y, oy);
  const qz = new Quaternion().setFromAxisAngle(AX_Z, oz);

  const q = new Quaternion();
  switch (order) {
    case 'XYZ':
      return q.copy(qx).multiply(qy).multiply(qz);
    case 'XZY':
      return q.copy(qx).multiply(qz).multiply(qy);
    case 'YXZ':
      return q.copy(qy).multiply(qx).multiply(qz);
    case 'YZX':
      return q.copy(qy).multiply(qz).multiply(qx);
    case 'ZXY':
      return q.copy(qz).multiply(qx).multiply(qy);
    case 'ZYX':
      return q.copy(qz).multiply(qy).multiply(qx);
    default:
      return q.copy(qz).multiply(qx).multiply(qy);
  }
}
// #endregion

// #region MAIN ENTRY POINTS

export function poseFromSkeleton(
  skelBind: Skeleton,
  skel: Skeleton,
  opts?: {
    rotationOffsets?: Record<string, [number?, number?, number?]>;
    channelOrder?: EulerOrder;
    includePosList?: string[];
  },
) {
  const order = opts?.channelOrder || ORDER;
  const posList = opts?.includePosList ?? INC_POS_LIST;

  const str =
    genHierarchy(skelBind, posList, order) +
    NL +
    genSingleFrameMotion(skel, posList, order, opts?.rotationOffsets);

  return str;
}

// #endregion

// #region GENERATE HIERACHY DATA

export function genHierarchy(
  skel: Skeleton,
  posList: Array<string>,
  order: EulerOrder,
): string {
  return traverseBone(skel.bones[0], 0, posList, order);
}

function traverseBone(
  b: Bone,
  lvl: number,
  posList: Array<string>,
  order: EulerOrder,
) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Channel - Use ZXY rotation order to match BVH loader/S3 clips
  const ch = posList.includes(b.name)
    ? `CHANNELS 6 Xposition Yposition Zposition Zrotation Xrotation Yrotation`
    : `CHANNELS 3 Zrotation Xrotation Yrotation`;

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Offset
  const p = b.position;
  const offset = `OFFSET ${p.x.toFixed(6)} ${p.y.toFixed(6)} ${p.z.toFixed(6)}`;

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const nType = lvl > 0 ? 'JOINT' : 'ROOT';
  const intendA = '	'.repeat(lvl);
  const intendB = '	'.repeat(lvl + 1);
  const intendC = '	'.repeat(lvl + 2);
  const initN = lvl === 0 ? 'HIERARCHY' + NL : NL;

  let rtn = `${initN}${intendA}${nType} ${b.name}${NL}${intendA}{${NL}${intendB}${offset}${NL}${intendB}${ch}`;

  if (b.children.length > 0) {
    // Build the children nodes
    for (const c of b.children) {
      rtn += traverseBone(c as Bone, lvl + 1, posList, order);
    }
  } else {
    // Bone is a leaf node, need to add this extra bit
    rtn += `${NL}${intendB}End Site${NL}${intendB}{${NL}${intendC}OFFSET 0.000000 1.000000 0.000000${NL}${intendB}}`;
  }

  rtn += `${NL}${intendA}}`;

  return rtn;
}

// #endregion

// #region GENERATE MOTION DATA

/*** NOTES ***
Motion values are in a matrix
- Each row represents a frame
- Each column represents a channel ( position/rotation )
*/

function fFormat(v: number): string {
  // Avoid printing out -0.000000
  return Math.abs(v) < 0.000001 ? '0.000000' : v.toFixed(6);
}

function genSingleFrameMotion(
  skel: Skeleton,
  posList: Array<string>,
  order: EulerOrder,
  rotationOffsets?: Record<string, [number?, number?, number?]>,
): string {
  let row = '';
  const e = new Euler(0, 0, 0, order);

  for (const b of skel.bones) {
    // position
    if (posList.includes(b.name)) {
      const p = b.position; // local pos matches your runtime (pelvis only)
      row += `${fFormat(p.x)} ${fFormat(p.y)} ${fFormat(p.z)} `;
    }

    // rotation
    let q = b.quaternion.clone();

    const offs = rotationOffsets?.[b.name as keyof typeof rotationOffsets];
    if (offs) {
      const qOff = buildOffsetQuat(offs, order);
      q = qOff.invert().multiply(q); // Q_clean = Qoff^-1 * Q_local
    }

    // decompose using the same order as CHANNELS
    e.setFromQuaternion(q, order);

    // ORDER === 'ZXY' ⇒ write Z, X, Y
    row += `${fFormat(e.z * TO_DEG)} ${fFormat(e.x * TO_DEG)} ${fFormat(
      e.y * TO_DEG,
    )} `;
  }

  return `MOTION${NL}Frames: 1${NL}Frame Time: 1.000000${NL}${row.trim()}`;
}

// #endregion
